/**
 * Listagem de propostas — filtros, tabela expansível e ações.
 *
 * Consome `GET /propostas`, que devolve um item por proposta (o ponteiro da versão corrente), já
 * filtrado pelo servidor conforme o perfil. A tela **não** decide o que o usuário pode ver: ela
 * mostra o que a API devolveu.
 *
 * A situação exibida no selo combina duas informações distintas, e a distinção importa:
 *
 * - `situacao` do documento: rascunho, publicada ou substituída;
 * - vigência calculada a partir de `validaAte`: vigente, vence em breve ou vencida.
 *
 * Uma proposta vencida continua publicada e acessível — é o requisito 5. O selo sinaliza, não
 * bloqueia.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { calcularVigencia, descreverVigencia, pode, type EstadoVigencia } from '@emaster/dominio';

import { listarPropostas } from '../api/cliente.ts';
import { useAutenticacao } from '../autenticacao/contexto.tsx';
import { Se } from '../autenticacao/Guardas.tsx';

// ---------------------------------------------------------------------------
// Tipos da resposta
// ---------------------------------------------------------------------------

interface Financeiro {
  readonly mensalidadeUsd: number;
  readonly mensalidadeBrl: number | null;
  readonly consumoUsd: number;
  readonly suporteUsd: number;
  readonly deployUsd: number;
  readonly creditoUsd: number;
  readonly creditoJanelaMeses: number;
  readonly economiaPercentual: number;
  readonly margemPercentual: number;
  readonly planoSuporte: string;
  readonly trilha: string;
  readonly duracaoMeses: number;
  readonly estimado: boolean;
}

interface ItemLista {
  readonly numero: string;
  readonly situacao: string;
  readonly cliente: string;
  readonly projeto: string;
  readonly responsavelEmail: string;
  /** Nome de exibição do responsável. `null` no acervo legado, que não tem documento. */
  readonly responsavelNome?: string | null;
  readonly responsavelSub: string;
  readonly emitidaEm: string;
  readonly validaAte: string;
  readonly versao: number;
  readonly url: string;
  readonly financeiro: Financeiro | null;
  readonly legado: boolean;
}

type Ordenacao = 'recentes' | 'validade' | 'cliente';

// ---------------------------------------------------------------------------
// Ícones SVG inline
// ---------------------------------------------------------------------------

function IconeOlho() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
      className="h-4 w-4"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

function IconeLapis() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
      className="h-4 w-4"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Z"
      />
    </svg>
  );
}

function IconeCopiar() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
      className="h-4 w-4"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
      />
    </svg>
  );
}

function IconeCheck() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
      className="h-4 w-4 text-green-600"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Apresentação
// ---------------------------------------------------------------------------

/** Formata um valor numérico como USD no padrão brasileiro: US$ 1.234,56 */
function formatarUsd(v: number): string {
  return 'US$ ' + v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Data `AAAA-MM-DD` em `DD/MM/AAAA`, sem depender de fuso. */
function dataBr(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  return `${dia ?? ''}/${mes ?? ''}/${ano ?? ''}`;
}

/** Extrai nome legível a partir do email. */
function extrairNome(email: string): string {
  return (
    email
      .split('@')[0]
      ?.split('.')
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ') ?? email
  );
}

/**
 * Nome do responsável para exibição: "Everton Marques", nunca o e-mail.
 *
 * Prefere `responsavelNome`, que é o nome como a pessoa o cadastrou, congelado no documento. Só cai
 * para a dedução pelo e-mail quando ele não existe -- no acervo legado, que não tem documento, e
 * enquanto a API sem o campo novo não estiver publicada.
 */
function nomeResponsavel(item: {
  readonly responsavelNome?: string | null;
  readonly responsavelEmail: string;
}): string {
  const nome = item.responsavelNome;
  if (nome !== undefined && nome !== null && nome.trim() !== '') return nome;
  return extrairNome(item.responsavelEmail);
}

/**
 * Largura de cada coluna, compartilhada pelo `<thead>` e pelo corpo.
 *
 * O corpo de cada linha é um único `<td colSpan={8}>` com layout flex, porque o painel de detalhes
 * expandido mora dentro da mesma célula. O efeito colateral é que o corpo não participa do cálculo
 * de largura de colunas da tabela: sem largura declarada, cada linha alinhava as próprias colunas
 * pelo conteúdo, então um nome de cliente longo empurrava responsável, emissão e validade para a
 * direita e desalinhava a linha das vizinhas e do cabeçalho.
 *
 * Declarar a largura em um só lugar e usá-la nos dois é o que impede o cabeçalho e o corpo de
 * divergirem na próxima alteração.
 */
const COLUNA = {
  chevron: 'w-8',
  numero: 'w-40',
  responsavel: 'w-40',
  data: 'w-28',
  situacao: 'w-40',
  acoes: 'w-36',
} as const;

const ESTILO_VIGENCIA: Readonly<Record<EstadoVigencia, { rotulo: string; classe: string }>> = {
  vigente: { rotulo: 'Vigente', classe: 'bg-green-50 text-green-700 ring-green-600/20' },
  vence_em_breve: {
    rotulo: 'Vence em breve',
    classe: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  },
  vencida: { rotulo: 'Vencida', classe: 'bg-red-50 text-red-700 ring-red-600/20' },
};

function Selo({ children, classe }: { readonly children: string; readonly classe: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${classe}`}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tela
// ---------------------------------------------------------------------------

export function TelaPropostas() {
  const { sessao } = useAutenticacao();
  const veTodas = pode(sessao?.perfil ?? null, 'ver_todas');

  const [todosItens, setTodosItens] = useState<readonly ItemLista[]>([]);
  const [itens, setItens] = useState<readonly ItemLista[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<'todas' | EstadoVigencia>('todas');
  const [ordem, setOrdem] = useState<Ordenacao>('recentes');
  const [copiado, setCopiado] = useState<string | null>(null);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [comoUsuario, setComoUsuario] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);

    try {
      const r = await listarPropostas();

      if (!r.ok) {
        const dados = r.dados as { mensagem?: string; erro?: string };
        setErro(dados.mensagem ?? `Não foi possível carregar as propostas (HTTP ${r.status}).`);
        return;
      }

      const dados = r.dados as { itens?: readonly ItemLista[] };
      setTodosItens(dados.itens ?? []);
    } catch (e) {
      setErro(
        `Falha na comunicação com o servidor: ${e instanceof Error ? e.message : 'erro desconhecido'}`,
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Filtra pelo comoUsuario no cliente
  useEffect(() => {
    if (comoUsuario === '') {
      setItens(todosItens);
    } else {
      setItens(todosItens.filter((i) => i.responsavelSub === comoUsuario));
    }
  }, [todosItens, comoUsuario]);

  // Lista de responsáveis derivada de todos os itens
  const responsaveis = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const item of todosItens) {
      if (!mapa.has(item.responsavelSub)) {
        mapa.set(item.responsavelSub, nomeResponsavel(item));
      }
    }
    return [...mapa.entries()]
      .map(([sub, nome]) => ({ sub, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [todosItens]);

  /**
   * Anota cada item com a vigência.
   */
  const anotados = useMemo(() => {
    const hoje = new Date().toISOString().slice(0, 10);
    return itens.map((i) => ({ item: i, vigencia: calcularVigencia(i.validaAte, hoje) }));
  }, [itens]);

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    const filtrados = anotados.filter(({ item, vigencia }) => {
      if (filtro !== 'todas' && vigencia.estado !== filtro) return false;
      if (termo === '') return true;

      return (
        item.cliente.toLowerCase().includes(termo) ||
        item.projeto.toLowerCase().includes(termo) ||
        item.numero.toLowerCase().includes(termo) ||
        // O e-mail continua buscável mesmo sem estar na tela; o nome entrou porque é o que se vê.
        item.responsavelEmail.toLowerCase().includes(termo) ||
        nomeResponsavel(item).toLowerCase().includes(termo)
      );
    });

    const ordenados = [...filtrados];
    if (ordem === 'recentes') {
      /*
       * Ordena por data de emissão, e não pelo número.
       *
       * O número não é cronológico entre as duas origens: o acervo importado usa `LEG-NNNN` e o
       * construtor novo usa `EM-AAAA-NNN`. Em ordem decrescente de texto, `LEG` vem antes de `EM`,
       * então ordenar por número empurrava toda proposta emitida no construtor novo para o fim da
       * lista, atrás dos 97 registros do legado -- quem acabava de publicar não encontrava a
       * própria proposta e concluía que a publicação tinha falhado.
       *
       * O número entra apenas como desempate, para a ordem ser estável entre propostas emitidas no
       * mesmo dia.
       */
      ordenados.sort(
        (a, b) =>
          b.item.emitidaEm.localeCompare(a.item.emitidaEm) ||
          b.item.numero.localeCompare(a.item.numero),
      );
    } else if (ordem === 'validade') {
      ordenados.sort((a, b) => a.item.validaAte.localeCompare(b.item.validaAte));
    } else {
      ordenados.sort((a, b) => a.item.cliente.localeCompare(b.item.cliente, 'pt-BR'));
    }

    return ordenados;
  }, [anotados, busca, filtro, ordem]);

  const contagem = useMemo(() => {
    const c = { todas: anotados.length, vigente: 0, vence_em_breve: 0, vencida: 0 };
    for (const { vigencia } of anotados) c[vigencia.estado] += 1;
    return c;
  }, [anotados]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  function toggleExpansao(numero: string) {
    setExpandidos((prev) => {
      const novo = new Set(prev);
      if (novo.has(numero)) {
        novo.delete(numero);
      } else {
        novo.add(numero);
      }
      return novo;
    });
  }

  async function copiarLink(url: string, numero: string) {
    await navigator.clipboard.writeText(url);
    setCopiado(numero);
    setTimeout(() => {
      setCopiado(null);
    }, 2000);
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-brand-blue dark:text-white sm:text-2xl">
            Propostas
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {carregando
              ? 'Carregando...'
              : `${String(contagem.todas)} ${contagem.todas === 1 ? 'proposta' : 'propostas'}`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void carregar()}
            disabled={carregando}
            className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-semibold text-gray-600 dark:text-gray-200 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Atualizar
          </button>
          <Se acao="criar">
            <Link
              to="/nova"
              className="rounded-lg bg-brand-accent px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-orange-600"
            >
              Nova proposta
            </Link>
          </Se>
        </div>
      </div>

      {erro !== null && (
        <div role="alert" className="rounded-xl border-l-4 border-red-500 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-800">Não foi possível carregar</p>
          <p className="mt-1 text-sm text-red-700">{erro}</p>
          <button
            type="button"
            onClick={() => void carregar()}
            className="mt-3 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-col gap-3 rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 shadow-sm sm:p-4 sm:flex-row sm:flex-wrap sm:items-center">
        <input
          type="search"
          value={busca}
          onChange={(e) => {
            setBusca(e.target.value);
          }}
          placeholder="Buscar por cliente, projeto ou número"
          aria-label="Buscar propostas"
          className="min-w-0 flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-200 focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
        />

        <div
          className="flex flex-wrap items-center gap-1"
          role="group"
          aria-label="Filtrar por vigência"
        >
          {(['todas', 'vigente', 'vence_em_breve', 'vencida'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => {
                setFiltro(f);
              }}
              aria-pressed={filtro === f}
              className={[
                'rounded-lg px-2 py-1 text-[11px] font-semibold transition-colors sm:px-3 sm:py-1.5 sm:text-xs',
                filtro === f
                  ? 'bg-brand-blue text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600',
              ].join(' ')}
            >
              {f === 'todas' ? 'Todas' : ESTILO_VIGENCIA[f].rotulo} ({String(contagem[f])})
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          Ordenar
          <select
            value={ordem}
            onChange={(e) => {
              setOrdem(e.target.value as Ordenacao);
            }}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm text-gray-700 dark:text-gray-200 focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
          >
            <option value="recentes">Emissão (mais recente)</option>
            <option value="validade">Validade</option>
            <option value="cliente">Cliente</option>
          </select>
        </label>

        {veTodas && (
          <label className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            Visualizar como
            <select
              value={comoUsuario}
              onChange={(e) => {
                setComoUsuario(e.target.value);
              }}
              className="rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-2 py-1.5 text-sm"
            >
              <option value="">Todos os vendedores</option>
              {responsaveis.map((r) => (
                <option key={r.sub} value={r.sub}>
                  {r.nome}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* Lista */}
      {comoUsuario !== '' && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-200">
          Visualizando como {responsaveis.find((r) => r.sub === comoUsuario)?.nome ?? comoUsuario}.
          Esta é exatamente a lista que essa pessoa vê.
        </div>
      )}
      {!carregando && visiveis.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-12 text-center">
          <p className="font-semibold text-brand-blue dark:text-white">
            {contagem.todas === 0 ? 'Nenhuma proposta publicada ainda' : 'Nada encontrado'}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {contagem.todas === 0
              ? 'A primeira proposta publicada aparece aqui.'
              : 'Ajuste a busca ou o filtro de vigência.'}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
          <div className="overflow-x-auto">
            {/*
             * `table-fixed` com `min-w`: as larguras declaradas passam a valer de fato, e em tela
             * estreita a tabela rola na horizontal em vez de comprimir as colunas de data até o
             * texto quebrar. O `overflow-x-auto` do contêiner acima é quem oferece a rolagem.
             */}
            <table className="w-full min-w-[40rem] table-fixed text-left text-sm">
              <thead className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <tr>
                  <th className={`${COLUNA.chevron} px-2 py-3`} />
                  <th scope="col" className={`${COLUNA.numero} px-4 py-3 font-semibold`}>
                    Número
                  </th>
                  <th scope="col" className="px-4 py-3 font-semibold">
                    Cliente e projeto
                  </th>
                  <th
                    scope="col"
                    className={`${COLUNA.responsavel} px-4 py-3 font-semibold hidden lg:table-cell`}
                  >
                    Responsável
                  </th>
                  <th
                    scope="col"
                    className={`${COLUNA.data} px-4 py-3 font-semibold whitespace-nowrap hidden lg:table-cell`}
                  >
                    Emissão
                  </th>
                  <th
                    scope="col"
                    className={`${COLUNA.data} px-4 py-3 font-semibold whitespace-nowrap`}
                  >
                    Validade
                  </th>
                  <th scope="col" className={`${COLUNA.situacao} px-4 py-3 font-semibold`}>
                    Situação
                  </th>
                  <th scope="col" className={`${COLUNA.acoes} px-4 py-3 text-right font-semibold`}>
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {visiveis.map(({ item, vigencia }) => {
                  const estilo = ESTILO_VIGENCIA[vigencia.estado];
                  const estaExpandido = expandidos.has(item.numero);

                  return (
                    <tr key={item.numero} className="group">
                      <td colSpan={8} className="p-0">
                        <div
                          className="flex cursor-pointer items-center transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
                          role="button"
                          tabIndex={0}
                          aria-expanded={estaExpandido}
                          onClick={() => {
                            toggleExpansao(item.numero);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleExpansao(item.numero);
                            }
                          }}
                        >
                          {/* Chevron */}
                          <div
                            className={`flex ${COLUNA.chevron} shrink-0 items-center justify-center px-2 py-3`}
                          >
                            <svg
                              className={`h-4 w-4 text-gray-500 transition-transform ${estaExpandido ? 'rotate-90' : ''}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </div>

                          {/* Número */}
                          <div className={`${COLUNA.numero} shrink-0 px-4 py-3`}>
                            <span className="font-semibold text-brand-blue dark:text-white whitespace-nowrap">
                              {item.numero}
                            </span>
                            {item.versao > 1 && (
                              <span className="ml-2 text-xs text-gray-400">v{item.versao}</span>
                            )}
                          </div>

                          {/*
                           * Cliente e projeto — a única coluna elástica, e a única que trunca.
                           *
                           * `min-w-0` é obrigatório: um item flex não encolhe abaixo do próprio
                           * conteúdo por padrão, então sem isso o `truncate` nunca dispara e o
                           * texto volta a empurrar as colunas seguintes. O `title` mantém o
                           * conteúdo integral acessível ao apontar o mouse.
                           */}
                          <div className="min-w-0 flex-1 px-4 py-3">
                            <p
                              className="truncate font-medium text-gray-800 dark:text-gray-200"
                              title={item.cliente}
                            >
                              {item.cliente}
                            </p>
                            <p
                              className="truncate text-xs text-gray-500 dark:text-gray-400"
                              title={item.projeto}
                            >
                              {item.projeto}
                            </p>
                          </div>

                          {/* Responsável — nome na tela, e-mail no title, para não perder o dado. */}
                          <div
                            className={`${COLUNA.responsavel} shrink-0 px-4 py-3 text-xs text-gray-500 dark:text-gray-400 hidden lg:block`}
                          >
                            <span className="block truncate" title={item.responsavelEmail}>
                              {nomeResponsavel(item)}
                            </span>
                          </div>

                          {/* Emissão */}
                          <div
                            className={`${COLUNA.data} shrink-0 px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-200 hidden lg:block`}
                          >
                            {dataBr(item.emitidaEm)}
                          </div>

                          {/* Validade */}
                          <div
                            className={`${COLUNA.data} shrink-0 px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-200`}
                          >
                            {dataBr(item.validaAte)}
                          </div>

                          {/* Situação */}
                          <div className={`${COLUNA.situacao} shrink-0 px-4 py-3`}>
                            {/* `items-start`: agora que a coluna tem largura fixa, sem isso os
                                selos esticariam até a borda em vez de acompanhar o texto. */}
                            <div className="flex flex-col items-start gap-1">
                              <Selo classe={estilo.classe}>{estilo.rotulo}</Selo>
                              <span className="text-xs text-gray-400">
                                {descreverVigencia(vigencia)}
                              </span>
                              {item.legado && (
                                <Selo classe="bg-slate-100 text-slate-600 ring-slate-500/20">
                                  Legado
                                </Selo>
                              )}
                              {item.situacao !== 'publicada' && !item.legado && (
                                <Selo classe="bg-gray-100 text-gray-600 ring-gray-500/20">
                                  {item.situacao}
                                </Selo>
                              )}
                            </div>
                          </div>

                          {/* Ações — ícones */}
                          <div className={`${COLUNA.acoes} shrink-0 px-4 py-3`}>
                            <div
                              className="flex items-center justify-end gap-1 whitespace-nowrap"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onKeyDown={(e) => {
                                e.stopPropagation();
                              }}
                              role="group"
                              aria-label="Ações da proposta"
                            >
                              {item.legado && vigencia.estado === 'vencida' ? (
                                <span
                                  className="rounded-lg p-2 text-brand-blue dark:text-blue-400 opacity-40 cursor-not-allowed"
                                  title="A proposta venceu e o construtor antigo bloqueia o acesso"
                                  aria-label="Abrir proposta (indisponível)"
                                >
                                  <IconeOlho />
                                </span>
                              ) : (
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Abrir proposta"
                                  aria-label="Abrir proposta"
                                  className="rounded-lg p-2 text-brand-blue dark:text-blue-400 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  <IconeOlho />
                                </a>
                              )}
                              {item.legado ? (
                                <span
                                  className="rounded-lg p-2 text-brand-accent opacity-40 cursor-not-allowed"
                                  title="Proposta importada do construtor antigo: não é editável"
                                  aria-label="Editar (indisponível)"
                                >
                                  <IconeLapis />
                                </span>
                              ) : (
                                <Link
                                  to={`/editar/${encodeURIComponent(item.numero)}`}
                                  title="Editar"
                                  aria-label="Editar"
                                  className="rounded-lg p-2 text-brand-accent transition-colors hover:bg-orange-50 dark:hover:bg-gray-700"
                                >
                                  <IconeLapis />
                                </Link>
                              )}
                              <button
                                type="button"
                                onClick={() => void copiarLink(item.url, item.numero)}
                                title="Copiar link"
                                aria-label="Copiar link"
                                className="rounded-lg p-2 text-gray-600 dark:text-gray-400 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                {copiado === item.numero ? <IconeCheck /> : <IconeCopiar />}
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Painel expandido com detalhes financeiros */}
                        {estaExpandido && item.financeiro !== null && (
                          <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-4 sm:px-8 lg:px-12">
                            {item.financeiro.estimado && (
                              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                                Valores importados do construtor antigo. São o resumo que o sistema
                                anterior preservou, sem as premissas de cálculo — então não são
                                recalculáveis nem editáveis.
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                  Plano de Suporte
                                </p>
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                  {item.financeiro.planoSuporte}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                  Trilha
                                </p>
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                  {item.financeiro.trilha}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                  Duração
                                </p>
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                  {String(item.financeiro.duracaoMeses)} meses
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                  Consumo AWS
                                </p>
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                  {formatarUsd(item.financeiro.consumoUsd)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                  Suporte
                                </p>
                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                  {formatarUsd(item.financeiro.suporteUsd)}
                                </p>
                              </div>
                              {!item.financeiro.estimado && (
                                <div>
                                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Deploy
                                  </p>
                                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                    {item.financeiro.creditoUsd > 0 && item.financeiro.deployUsd > 0
                                      ? 'Coberto pelo crédito'
                                      : formatarUsd(item.financeiro.deployUsd)}
                                  </p>
                                </div>
                              )}
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                  Crédito AWS
                                </p>
                                <p className="text-sm font-semibold text-brand-accent">
                                  -{formatarUsd(item.financeiro.creditoUsd)} (
                                  {String(item.financeiro.creditoJanelaMeses)} meses)
                                </p>
                              </div>
                              {!item.financeiro.estimado && (
                                <div>
                                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Economia
                                  </p>
                                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                    {item.financeiro.economiaPercentual.toLocaleString('pt-BR', {
                                      minimumFractionDigits: 1,
                                      maximumFractionDigits: 1,
                                    })}
                                    %
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="mt-4 rounded-lg bg-brand-blue/5 dark:bg-brand-blue/20 px-4 py-3">
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                Investimento Mensal
                              </p>
                              <p className="text-lg font-bold text-brand-blue dark:text-white">
                                {formatarUsd(item.financeiro.mensalidadeUsd)}
                              </p>
                            </div>
                          </div>
                        )}

                        {estaExpandido && item.financeiro === null && (
                          <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-4 sm:px-8 lg:px-12">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Proposta importada do construtor antigo. As premissas de cálculo não
                              foram preservadas pelo sistema anterior, então os valores não estão
                              disponíveis.
                            </p>
                            {item.legado && vigencia.estado !== 'vencida' && item.url !== '' && (
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 inline-block text-sm font-medium text-brand-blue hover:underline dark:text-blue-400"
                              >
                                Abrir proposta no construtor antigo →
                              </a>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400">
        Proposta vencida continua acessível ao cliente: o selo sinaliza, não bloqueia.
      </p>
    </div>
  );
}
